import {useEffect, useState} from "react";
import {
    createSelectors,
    createStoreWithName,
    createStoreWithStorageNameAndBlacklist,
    createStoreWithStorageNameBlacklistAndVersion,
} from "./base";
import { request } from "@/utils/useRequest";
import _ from "lodash";

const initState = {
    inited: false,
    area:[],
    areatree:null,
    rolelist:[],
    sysdicts:[],
    menus:[],
    side_menus: [], // 侧边菜单
    user: null,
    topmenu_id:0,
    route_navigate: undefined,
    // Redux common reducer 兼容字段
    token: null,
    required: [{ Id: true, Name: '是' }, { Id: false, Name: '否' }],
    windowHeight: window.innerHeight,
    windowWidth: window.innerWidth,
    siderCollapsed: false,
    reading_type: [], // 采集指标类型
    sensor_model_type: [], // 传感器类型
    gis_codetab: [], // 附属物类型
    resloding: false,
    loadingmessage: "页面加载中...",
    kpiPeriod: [
        { name: "实时", value: "realtime", sn: 0 },
        { name: "5分钟", value: "t-5", sn: 1 },
        { name: "15分钟", value: "t-15", sn: 2 },
        { name: "30分钟", value: "t-30", sn: 3 },
        { name: "60分钟", value: "t-60", sn: 4 },
        { name: "日", value: "t-1440", sn: 5 },
        { name: "5分钟", value: "k-5", sn: 6 },
        { name: "15分钟", value: "k-15", sn: 7 },
        { name: "30分钟", value: "k-30", sn: 8 },
        { name: "60分钟", value: "k-60", sn: 9 },
        { name: "日", value: "k-1440", sn: 10 }
    ],
    curTab: [], // 当前tab页面
    theme: '', // 网站主题
    primaryColor: '#1D6FF0', // 主题色
    themeMode: 'auto', // 主题大小模式：'small' | 'medium' | 'large' | 'auto'
    reloadPath: 'null', // 需要刷新的tab路径
    tags: [], // 标签列表
    dtutree: null, // DTU树
    orgalltree: null, // 组织树
    sensormodellist: [], // 传感器模型列表
    calclist: [], // 计算列表
    // 基本信息（配置字段，不随动态数据刷新）
    sensorInfoMap: {}, // { [sensorId]: { ...基本信息/配置... } }
    readInfoMap: {},   // { [readingId]: { ...基本信息/配置（含 store 等配置字段）... } }
    // 动态数据（随 refresh-data 刷新，只含数据类字段）
    sensorDataMap: {}, // { [sensorId]: { ...动态数据... } }
    readDataMap: {},   // { [readingId]: { ...动态数据（data/history7d）... } }
};

// const createStore = createStoreWithName('jymt-common-storage');
// 版本号：当需要让老版本客户端失效时，增加此版本号
// v3: store 字段从 readDataMap（动态数据）迁移到 readInfoMap（配置字段），
//     旧持久化数据中 store 位置不对，通过版本号失效应掉
const STORE_VERSION = 3;
const createStore = createStoreWithStorageNameBlacklistAndVersion('kzpipe-common-storage', ['inited'], STORE_VERSION);

export const commonBaseStore =
    createStore(
        (set, get) => ({
            ...initState,
            /**
             * 获取所有传感器（合并基本信息和动态数据，返回数组）
             */
            getAllSensors: () => {
                const info = get().sensorInfoMap;
                const data = get().sensorDataMap;
                return Object.keys(info).map(id => ({ ...info[id], ...(data[id] || {}) }));
            },
            /**
             * 获取单个传感器（合并基本信息和动态数据）
             */
            getSensorById: (sensorId) => {
                const info = get().sensorInfoMap[sensorId] || {};
                const data = get().sensorDataMap[sensorId] || {};
                return { ...info, ...data };
            },
            /**
             * 获取所有指标（合并基本信息和动态数据，返回数组）
             */
            getAllReadings: () => {
                const info = get().readInfoMap;
                const data = get().readDataMap;
                return Object.keys(info).map(id => ({ ...info[id], ...(data[id] || {}) }));
            },
            /**
             * 获取单个指标（合并基本信息和动态数据）
             */
            getReadingById: (readingId) => {
                const info = get().readInfoMap[readingId] || {};
                const data = get().readDataMap[readingId] || {};
                return { ...info, ...data };
            },
            /**
             * 获取某传感器的所有指标（返回合并后的数组）
             */
            getReadingsBySensorId: (sensorId) => {
                const info = get().readInfoMap;
                const data = get().readDataMap;
                return Object.keys(info)
                    .filter(id => info[id].sensorId === sensorId)
                    .map(id => ({ ...info[id], ...(data[id] || {}) }));
            },
            /**
             * 获取指定reading的近7天历史数据（自动补齐）
             * @param {string} readingId
             * @returns {Promise<Array>} 近7天数据数组
             */
            getReadingHistory7d: async (readingId) => {
                const reading = get().readDataMap[readingId];
                if (!reading) return null;
                if (!reading.history7d || reading.history7d.length < 7) {
                    // 拉取近7天数据，假设API为 /api/v2/private/reading/history
                    const result = await request("/api/v2/private/reading/history", "POST", "json", { readingId, days: 7 });
                    if (result.success) {
                        reading.history7d = result.data;
                        set(state => {
                            state.readDataMap = { ...state.readDataMap, [readingId]: { ...reading } };
                        });
                    } else {
                        reading.history7d = [];
                    }
                }
                return reading.history7d;
            },
            /**
             * 可选：批量补齐所有reading的近7天数据
             */
            fetchAllReadingsHistory7d: async () => {
                const readDataMap = get().readDataMap;
                for (const readingId in readDataMap) {
                    const reading = readDataMap[readingId];
                    if (!reading.history7d || reading.history7d.length < 7) {
                        await get().getReadingHistory7d(readingId);
                    }
                }
            },
            login: async (uid, pwd) => {
                // const { request } = UseRequest();
                // const requestUrl = process.env.NODE_ENV === "production"?"/Customer/AjaxLogin":"https://www.jinyugou.cn/Customer/AjaxLogin"
                const requestUrl = '/api/v3/public/login';
                const result = await request(requestUrl, "POST", "json", { uid, pwd, rememberMe:true, returnUrl:"/" });
                if (result.success) {
                    //localStorage.user = JSON.stringify(result.data);
                    set(state => {
                        state.user = {
                            ...result.data,
                            //auth:{name:result.data.Id,token:result.data.token}
                        };
                    });
                }
                return result;
            },
            logout: () => {
                set(state => {
                    state.user = null;
                });
                return true;
            },
            initFn: async () => {
                const result = await request("/api/v3/private/initdata/get", "POST", "json", { });
                if (result.success) {
                    // 地区树
                    let filter1 = (pid) => {
                        let tt = _.filter(result.data.Area, (item) => { return item.ParentCode == pid });
                        let st = _.sortBy(tt, "Code");
                        st.forEach(itm => {
                            itm.children = filter1(itm.Code);
                        });
                        return st;
                    };
                    let areatree = filter1("000000");

                    // 拆分sensorList
                    const sensorInfoMap = {};
                    const sensorDataMap = {};
                    (result.data.sensorList || []).forEach(item => {
                        const { sensorId, sensorName, address, number, ext, sn, model_tags, alias, coord, defined,
                            model_id, model_produce, model_name,
                            dtu_model_id, dtu_model_produce, dtu_model_name } = item;
                        sensorInfoMap[sensorId] = {
                            sensorId, sensorName, address, number, ext, sn, model_tags, alias, coord, defined,
                            model_id, model_produce, model_name,
                            dtu_model_id, dtu_model_produce, dtu_model_name
                        };
                        // 动态数据字段
                        const { time, next, readTime, interval_read, interval_upload, memo, icon, expand } = item;
                        sensorDataMap[sensorId] = { time, next, readTime, interval_read, interval_upload, memo, icon, expand };
                    });
                    // 拆分readList
                    const readInfoMap = {};
                    const readDataMap = {};
                    (result.data.ReadList || []).forEach(item => {
                        // store 是指标配置字段（是否启用存储），不随动态数据变化，归入 readInfoMap
                        const { id, sensorId, sensorName, readingId, reading, readingName, sn, unit, interval_read, interval_upload, type, store } = item;
                        readInfoMap[id] = { id, sensorId, sensorName, readingId, reading, readingName, sn, unit, interval_read, interval_upload, type, store };
                        // 动态数据字段
                        const { data, history7d } = item;
                        readDataMap[id] = { data, history7d };
                    });

                    set(state => {
                        const tags = result.data.tags ? result.data.tags.map(item => ({ ...item, enabled: true })) : [];
                        state.user = {...result.data.user,auth:state.user.auth};
                        state.area = result.data.Area;
                        state.rolelist = result.data.CustomerRole;
                        state.sysdicts = result.data.SysDict;
                        state.areatree = areatree;
                        state.inited = true;
                        state.tags = tags;
                        state.calclist = result.data.calclist || [];
                        state.gis_codetab = result.data.gis_codetab || [];
                        state.reading_type = result.data.reading_type || [];
                        state.sensor_model_type = result.data.sensor_model_type || [];
                        state.sensorInfoMap = sensorInfoMap;
                        state.sensorDataMap = sensorDataMap;
                        state.readInfoMap = readInfoMap;
                        state.readDataMap = readDataMap;
                    });
                }
            },
            refreshData: async () => {
                const result = await request("/api/v3/private/initdata/refresh-data", "POST", "json", { });
                if (result.success) {
                    // 只更新动态数据
                    const sensorDataMap = {};
                    (result.data.sensors || []).forEach(item => {
                        const { sensorId } = item;
                        if (sensorId) {
                            const { time, next, readTime, interval_read, interval_upload, memo, icon, expand } = item;
                            sensorDataMap[sensorId] = { time, next, readTime, interval_read, interval_upload, memo, icon, expand };
                        }
                    });
                    const readDataMap = {};
                    (result.data.readlist || []).forEach(item => {
                        // 只更新动态数据；store 是配置字段，保留在 readInfoMap 中不随刷新变化
                        const { id, data, history7d } = item;
                        if (id) readDataMap[id] = { data, history7d };
                    });
                    set(state => {
                        state.sensorDataMap = sensorDataMap;
                        state.readDataMap = readDataMap;
                    });
                }
            },
            getMenu: async () => {
                const result = await request("/api/v3/private/menu/all", "POST", "json", { });
                if (result.success) {
                    //localStorage.user = JSON.stringify(result.data);
                    //state.inited = true;
                    set(state => {
                        state.menus = result.data;
                    });
                }
                return result;
            },
            setTopMenuCurrentId:(id) => {
                set(state => {
                    state.topmenu_id = id;
                });
            },
            getTopMenuFirstPage:(id) => {
                get(state=>{
                    
                });
            },
            setRouteNavigate(navigate) {
                set(state => {
                    state.route_navigate = navigate;
                })
            },

            setPrimaryColor: (color) => {
                set(state => {
                    state.primaryColor = color;
                });
            },

            setThemeMode: (mode) => {
                set(state => {
                    state.themeMode = mode;
                });
            },

            // Redux common reducer 兼容 actions
            networkError: () => {
                set(state => {
                    state.resloding = false;
                });
            },

            initData: (data) => {
                set(state => {
                    state.user = data;
                });
            },

            updateSensor: (data) => {
                set(state => {
                    // 更新基本信息字典中的 alias
                    if (state.sensorInfoMap && state.sensorInfoMap[data.sensorId]) {
                        state.sensorInfoMap[data.sensorId].alias = data.alias;
                    }
                });
            },

            dtutreeRefreshSuccess: (data) => {
                set(state => {
                    state.dtutree = data;
                });
            },

            stdListSuccess: (data, kindof) => {
                set(state => {
                    if (kindof === "ORGALLTREE") {
                        state.orgalltree = data;
                    }
                });
            },

            showResourceLoading: (showOrHide, message) => {
                set(state => {
                    state.resloding = showOrHide;
                    state.loadingmessage = message || "页面加载中...";
                });
            },

            loginSuccess: (data) => {
                set(state => {
                    localStorage.user = JSON.stringify(data);
                    state.user = data;
                });
            },

            getProfileSuccess: (data) => {
                set(state => {
                    state.user = data;
                });
            },

            logoutAction: () => {
                set(state => {
                    localStorage.removeItem("user");
                    state.token = null;
                    state.user = null;
                });
            },

            resize: (winHeight, winWidth) => {
                set(state => {
                    state.windowHeight = winHeight;
                    state.windowWidth = winWidth;
                });
            },

            setSiderCollapsed: (collapsed) => {
                set(state => {
                    state.siderCollapsed = collapsed;
                });
            },

            changeAvatarSuccess: (data) => {
                set(state => {
                    const tempuser = _.clone(state.user);
                    tempuser.HeadImgUrl = data;
                    localStorage.user = JSON.stringify(tempuser);
                    state.user = tempuser;
                });
            },

            getMenuSuccess: (data) => {
                set(state => {
                    state.menus = data;
                });
            },

            getSideMenuSuccess: (data) => {
                set(state => {
                    state.side_menus = data;
                });
            },

            refreshSensorsAllSuccess: (data) => {
                set(state => {
                    state.readlist = [...(data.ReadList || [])];
                    state.sensorlist = [...(data.sensorList || [])];
                });
            },

            refreshReadingList: (data) => {
                set(state => {
                    state.readlist = data;
                });
            },

            setTabs: (data) => {
                set(state => {
                    state.curTab = data;
                });
            },

            refreshSensors: (data) => {
                set(state => {
                    // 指标列表
                    const index = _.findIndex(state.readlist, { sensorId: data.sensor, reading: data.reading });
                    if (index >= 0) {
                        state.readlist[index] = { ...state.readlist[index], data: data };
                    }
                    // 传感器列表
                    const s_index = _.findIndex(state.sensorlist, { sensorId: data.sensor });
                    if (s_index >= 0) {
                        state.sensorlist[s_index] = { ...state.sensorlist[s_index], time: data.readTime };
                    }
                });
            },

            updateAppendageTypeStatus: (data) => {
                set(state => {
                    const target = _.find(state.sysdicts, { Name: "AppendageType" });
                    if (target) {
                        const index = _.findIndex(target.Data, { code: data.code });
                        if (index >= 0) {
                            target.Data[index] = { ...target.Data[index], enabled: data.enabled };
                        }
                    }
                });
            },

            updateCodetabStatus: (data) => {
                set(state => {
                    const index = _.findIndex(state.gis_codetab, { code: data.code });
                    if (index >= 0) {
                        state.gis_codetab[index] = { ...state.gis_codetab[index], enabled: data.enabled };
                    }
                });
            },

            updateGisCodetabStatus: (code, enabled) => {
                set(state => {
                    const index = _.findIndex(state.gis_codetab, { code });
                    if (index >= 0) {
                        state.gis_codetab[index] = { ...state.gis_codetab[index], enabled };
                    }
                });
            },

            updateTagsStatus: (data) => {
                set(state => {
                    const index = _.findIndex(state.tags, { type: 'sensor_model', title: data.code });
                    if (index >= 0) {
                        state.tags[index] = { ...state.tags[index], enabled: data.enabled };
                    }
                });
            },
        })
    ) // create
;

export const useCommonStore = createSelectors(commonBaseStore);

// 推荐用法示例：
// const sensors = useCommonStore.getState().getAllSensors();
// const sensor = useCommonStore.getState().getSensorById(sensorId);
// const readings = useCommonStore.getState().getReadingsBySensorId(sensorId);

export const useHydration = () => {
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        // Note: This is just in case you want to take into account manual rehydration.
        // You can remove the following line if you don't need it.
        // const unsubHydrate = useBoundStore.persist.onHydrate(() => setHydrated(false))
        const unsubFinishHydration = useCommonStore.persist.onFinishHydration(() => setHydrated(true))
        setHydrated(useCommonStore.persist.hasHydrated())
        return () => unsubFinishHydration();
    }, []);

    return hydrated
}